import React from "react";
import { Avatar, Button, Space, Tag, Tooltip, Typography } from "antd";
import { t } from "i18next";
import {
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    MedicineBoxOutlined,
    BankOutlined,
    IdcardOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text } = Typography;

/**
 * Randevu mobil kartı — hem AppointmentDataGrid (genel liste) hem
 * AppointmentPatientDataGrid (hasta kartı içi) tarafından kullanılır.
 *
 * showPatient=false → hasta başlığı gizlenir (hasta bağlamında zaten tek hasta).
 *                     O durumda başlık olarak tarih/saat gösterilir.
 * showPreview + onPreview + canPreview → sağ üstte önizleme butonu.
 */
export default function AppointmentCard({
    row,
    formatPrice,
    showPatient = true,
    showPreview = false,
    canPreview = false,
    onPreview,
}) {
    const hasDebt = row.balance > 0;
    const currency = row?.currency || 'TRY';
    const priceHidden = row.totalPrice === -1;
    const fmt = (v) => (v === -1 ? '-' : formatPrice(v, { currency }));

    return (
        <div>
            {/* ÜST satır. Hasta görünürken: [hasta] solda, [durum+preview] sağda.
                Hasta gizliyken: [durum] solda, [preview] sağda. */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: showPatient ? 'flex-start' : 'center',
                gap: 8,
                marginBottom: 10
            }}>
                {showPatient ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                            <Avatar size={32} icon={<UserOutlined />} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>
                                    {row?.patientName} {row?.patientLastname}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#8c8c8c' }}>
                                    <IdcardOutlined />
                                    <span>{row?.patientCustomId}</span>
                                    {row?.otherPatientId && (
                                        <Tag style={{ fontSize: 10, lineHeight: '14px', margin: 0, padding: '0 4px' }}>
                                            {t('Other')}: {row.otherPatientId}
                                        </Tag>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            {row.stateName && (
                                <Tag color={row.stateColor || 'default'} style={{ margin: 0 }}>
                                    {row.stateName}
                                </Tag>
                            )}
                            {showPreview && (
                                <Tooltip title={t('Appointment Preview')}>
                                    <Button
                                        size="small"
                                        type="primary"
                                        ghost
                                        icon={<EyeOutlined />}
                                        disabled={!canPreview}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPreview && onPreview(row);
                                        }}
                                    >
                                        {t('Preview')}
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {/* Status solda */}
                        {row.stateName ? (
                            <Tag color={row.stateColor || 'default'} style={{ margin: 0 }}>
                                {row.stateName}
                            </Tag>
                        ) : <span />}
                        {/* Preview sağda */}
                        {showPreview && (
                            <Tooltip title={t('Appointment Preview')}>
                                <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    icon={<EyeOutlined />}
                                    disabled={!canPreview}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPreview && onPreview(row);
                                    }}
                                >
                                    {t('Preview')}
                                </Button>
                            </Tooltip>
                        )}
                    </>
                )}
            </div>

            {/* ORTA: Tarih + Saat — her zaman görünür */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#fafafa',
                padding: '6px 10px',
                borderRadius: 4,
                marginBottom: 8,
                fontSize: 13
            }}>
                <Space size={6}>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{moment(row.startDate).format('DD.MM.YYYY')}</Text>
                </Space>
                <Space size={6}>
                    <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                    <Text type="secondary">
                        {moment(row.startDate).format('HH:mm')} - {moment(row.endDate).format('HH:mm')}
                    </Text>
                </Space>
            </div>

            {/* BİRİM + (KURUM • TİP tek satır) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, fontSize: 13 }}>
                {row.unitName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1890ff' }}>
                        <MedicineBoxOutlined />
                        <span>{row.unitName}</span>
                    </div>
                )}
                {(row.institutionName || row.appointmentTypeName) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959' }}>
                        <BankOutlined />
                        <Text type="secondary">
                            {/* "Özel (Ayaktan)" formatı: kurum + parantez içinde randevu türü */}
                            {row.institutionName || ''}
                            {row.institutionName && row.appointmentTypeName ? ' ' : ''}
                            {row.appointmentTypeName ? `(${row.appointmentTypeName})` : ''}
                        </Text>
                    </div>
                )}
                {/* İşlemler — kurum altında "|" ile ayrılmış (backend ", " gönderir) */}
                {row.operations && (
                    <div style={{ color: '#8c8c8c', fontSize: 12, paddingLeft: 22 }}>
                        {row.operations}
                    </div>
                )}
            </div>

            {/* ALT: Finansal */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px dashed #f0f0f0',
                paddingTop: 8,
                gap: 8
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#8c8c8c', minWidth: 0 }}>
                    <span>
                        {t('Price')}: <Text style={{ fontSize: 12, color: '#595959' }}>{fmt(row.totalPrice)}</Text>
                    </span>
                    <span>
                        {t('Paid')}: <Text style={{ fontSize: 12, color: row.totalPaid > 0 ? '#389e0d' : '#595959' }}>{fmt(row.totalPaid)}</Text>
                    </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#8c8c8c' }}>{t('Balance')}</div>
                    <div style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: priceHidden ? '#8c8c8c' : (hasDebt ? '#cf1322' : '#389e0d')
                    }}>
                        {priceHidden ? '-' : formatPrice(row.balance, { currency })}
                    </div>
                </div>
            </div>
        </div>
    );
}
